import { useState } from 'react'

import { api, downloadBlob } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, timeAgo } from '../common'
import { DataTable } from '../DataTable'

/**
 * «Sigilli e archivio» (ADR-0030, «come rendiamo immutabili i log?»): the
 * tenant's seal key and its fingerprint, the seals (the chain's head signed
 * every hour, time-stamped once a day by the tenant's authority), and the
 * copy on the tenant's archive probe with its last re-reading. The officer
 * seals now or rotates the key; anyone in the area exports the seals to
 * verify them outside CENSO.
 */

function dimensione(byte: number): string {
  if (byte < 1024) return `${byte} B`
  if (byte < 1024 * 1024) return `${(byte / 1024).toFixed(1)} kB`
  return `${(byte / 1024 / 1024).toFixed(1)} MB`
}

export function VistaSigilli({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.accessSeals(), [])
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [ruota, setRuota] = useState(false)

  async function esegui(fn: () => Promise<unknown>) {
    setBusy(true)
    setErrore(null)
    try {
      await fn()
      setRuota(false)
      void dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function esporta() {
    try {
      const blob = await downloadBlob(api.accessSealsExportPath())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-access-sigilli-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setErrore(e)
    }
  }

  const d = dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d) return <ErrorBanner error={dati.error} />
  const s = d.state
  const a = d.archive
  return (
    <>
      <Card title={t('Sigilli')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Ogni ora la testa della catena è firmata con la chiave del tenant, che non sta nel database: chi avesse solo il database e riscrivesse eventi e impronte non potrebbe rifare la firma, e «Verifica adesso» lo direbbe. Una volta al giorno, se è impostata una TSA, il sigillo riceve la marca temporale RFC 3161: una data certa data da terzi.')}
        </p>
        <dl className="kv kv--attributi">
          <dt>{t('Chiave del tenant')}</dt>
          <dd>{s.fingerprint ? <><span className="mono">{s.fingerprint}</span> <span className="muted">· {t('versione {n}', { n: s.key_version })}</span></> : '—'}</dd>
          <dt>{t('Chiave pubblica')}</dt><dd className="mono" style={{ wordBreak: 'break-all' }}>{s.public_key ?? '—'}</dd>
          <dt>{t('Ultimo sigillo')}</dt>
          <dd>{s.last_seal ? <>{formatDateTime(s.last_seal.at)} <span className="muted">· {t('fino all’evento {seq}', { seq: s.last_seal.up_to_seq })}</span></> : <span className="muted">{t('nessuno')}</span>}</dd>
          <dt>{t('Marca temporale')}</dt>
          <dd>
            {!s.tsa_url ? <span className="muted">{t('nessuna TSA impostata (Impostazioni)')}</span>
              : s.last_stamp?.stamped ? <>{formatDateTime(s.last_stamp.tsa_time)} <span className="muted">· {s.last_stamp.tsa_signer ?? s.tsa_url}</span></>
                : s.last_stamp?.tsa_error ? <span className="eagle-motivo">{s.last_stamp.tsa_error}</span>
                  : <span className="muted">{t('alla prossima occasione')}</span>}
          </dd>
        </dl>
        <ErrorBanner error={errore} />
        <div className="form-dialog__azioni" style={{ justifyContent: 'flex-start' }}>
          {scrive && <button type="button" onClick={() => void esegui(() => api.accessSealNow())} disabled={busy}>{t('Sigilla adesso')}</button>}
          <button type="button" className="secondary" onClick={() => void esporta()}>{t('Esporta i sigilli')}</button>
          {scrive && !ruota && <button type="button" className="secondary" onClick={() => setRuota(true)} disabled={busy}>{t('Ruota la chiave')}</button>}
          {scrive && ruota && (
            <>
              <span className="muted">{t('I sigilli già fatti restano verificabili con la loro versione; da adesso si firma con la nuova.')}</span>
              <button type="button" className="danger" onClick={() => void esegui(() => api.accessSealRotate())} disabled={busy}>{t('Conferma la rotazione')}</button>
              <button type="button" className="secondary" onClick={() => setRuota(false)}>{t('Annulla')}</button>
            </>
          )}
        </div>
        {d.seals.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <DataTable rows={d.seals} rowKey={(x) => x.id} conteggio={{ nome: t('sigilli') }} paginazione={{ predefinita: 10 }} columns={[
              { key: 'at', header: t('Quando'), type: 'date', value: (x) => x.at, render: (x) => formatDateTime(x.at) },
              { key: 'up_to_seq', header: t('Fino all’evento'), type: 'number', value: (x) => x.up_to_seq },
              { key: 'head_hash', header: t('Impronta'), value: (x) => x.head_hash, render: (x) => <span className="mono" title={x.head_hash}>{x.head_hash.slice(0, 16)}…</span> },
              { key: 'key_version', header: t('Chiave'), type: 'number', value: (x) => x.key_version },
              { key: 'stamp', header: t('Marca temporale'), filter: 'select', value: (x) => (x.stamped ? t('sì') : x.tsa_url ? t('non ottenuta') : t('no')),
                render: (x) => x.stamped ? <span title={`${x.tsa_signer ?? ''} · ${x.tsa_serial ?? ''}`}>{formatDateTime(x.tsa_time)}</span>
                  : x.tsa_error ? <small className="eagle-motivo">{x.tsa_error}</small> : <span className="muted">—</span> },
            ]} />
          </div>
        )}
      </Card>

      <Card title={t('Copia sulla sonda d’archivio')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Ogni giorno la parte nuova del registro va alla sonda d’archivio del tenant come segmento firmato: la sonda controlla firma e impronta, lo conserva in sola lettura (immutabile dove il file system lo permette) fino alla fine della conservazione, e ogni giorno lo rilegge. Un registro cancellato o riscritto nel Control Plane resta lì, e la rilettura lo dice.')}
        </p>
        {!a.probe_id ? (
          <EmptyState title={t('Nessuna sonda d’archivio.')} hint={t('Si sceglie in Impostazioni → Copia d’archivio, fra le sonde del tenant.')} />
        ) : (
          <>
            {(a.copies ?? []).length > 0 && (
              <DataTable rows={a.copies ?? []} rowKey={(c) => c.probe_id} paginazione={false} strumenti={false} columns={[
                { key: 'role', header: t('Copia'), value: (c) => ({ primary: t('principale'), secondary: t('seconda copia'), dr: t('disaster recovery') } as Record<string, string>)[c.role] ?? c.role },
                { key: 'probe_name', header: t('Sonda'), value: (c) => c.probe_name, render: (c) => <strong>{c.probe_name}</strong> },
                { key: 'segments_stored', header: t('Segmenti conservati'), type: 'number', value: (c) => c.segments_stored,
                  render: (c) => <>{c.segments_stored.toLocaleString()} <span className="muted">· {t('fino all’evento {seq}', { seq: c.stored_up_to_seq })}</span></> },
                { key: 'pending', header: t('In attesa'), type: 'number', value: (c) => c.pending },
                { key: 'last_check_at', header: t('Ultima rilettura'), type: 'date', value: (c) => c.last_check_at ?? '', render: (c) => (
                  <>{c.last_check_at ? <span title={formatDateTime(c.last_check_at)}>{timeAgo(c.last_check_at)}</span> : '—'}{c.problems > 0 && <> <span className="badge badge--error">{t('{n} segmenti con problemi', { n: c.problems })}</span></>}{c.lost > 0 && <> <span className="badge badge--warn">{t('{n} persi', { n: c.lost })}</span></>}</>
                ) },
              ]} />
            )}
            <dl className="kv kv--attributi">
              <dt>{t('Sonda')}</dt><dd>{a.probe_name}</dd>
              <dt>{t('Segmenti conservati')}</dt><dd>{a.segments_stored.toLocaleString()} <span className="muted">· {t('fino all’evento {seq}', { seq: a.stored_up_to_seq })}</span></dd>
              <dt>{t('In attesa')}</dt><dd>{a.pending}</dd>
              <dt>{t('Ultima rilettura')}</dt><dd>{a.last_check_at ? <span title={formatDateTime(a.last_check_at)}>{timeAgo(a.last_check_at)}</span> : '—'}{a.problems > 0 && <span className="badge badge--error" style={{ marginLeft: 8 }}>{t('{n} segmenti con problemi', { n: a.problems })}</span>}</dd>
              {a.lost > 0 && <><dt>{t('Persi')}</dt><dd className="eagle-motivo">{t('{n} segmenti non presi dalla sonda prima della fine della conservazione', { n: a.lost })}</dd></>}
            </dl>
            {a.segments.length > 0 && (
              <DataTable rows={a.segments} rowKey={(x) => x.id} conteggio={{ nome: t('segmenti') }} paginazione={{ predefinita: 10 }} columns={[
                { key: 'range', header: t('Eventi'), value: (x) => `${x.from_seq}–${x.to_seq}`, render: (x) => <span className="mono">{x.from_seq}–{x.to_seq}</span> },
                { key: 'count', header: t('Quanti'), type: 'number', value: (x) => x.count },
                { key: 'size', header: t('Dimensione'), type: 'number', value: (x) => x.size, render: (x) => dimensione(x.size) },
                { key: 'created_at', header: t('Tagliato'), type: 'date', value: (x) => x.created_at, render: (x) => formatDateTime(x.created_at) },
                { key: 'stored_at', header: t('Sulla sonda'), type: 'date', value: (x) => x.stored_at ?? '', render: (x) => x.stored_at ? formatDateTime(x.stored_at) : x.lost ? <span className="eagle-motivo">{t('perso')}</span> : <span className="muted">{t('in attesa')}</span> },
                { key: 'check', header: t('Rilettura'), filter: 'select', value: (x) => (x.check_ok === null ? '' : x.check_ok ? t('integro') : t('problema')),
                  render: (x) => x.check_ok === null ? <span className="muted">—</span> : x.check_ok ? <span className="badge badge--ok">{t('integro')}</span> : <span className="badge badge--error" title={x.check_detail ?? ''}>{x.check_detail ?? t('problema')}</span> },
              ]} />
            )}
          </>
        )}
      </Card>
    </>
  )
}
